import { SyntenyTypeEnum, TrackEnum } from "@selector/shared/openapi";
import { TOKEN_KEY } from "@selector/shared/settings";
import { debounce } from "lodash";
import { verticalState } from "widgets/utils";
import { colors } from "widgets/utils";
import type { StateCreator, StoreApi } from "zustand";

import {
  drawCoordinates,
  drawHorizontalLine,
  drawVerticalGridLines,
} from "../../main-canvas/graphics/coordinates-renderer.ts";
import { drawGroupHeader } from "../../main-canvas/graphics/group-header-renderer.ts";
import {
  drawAlignment,
  drawAlignmentImageBitmaps,
  drawDarkOverlay,
  drawHoveredFeatures,
  drawRangeSelection,
  drawTrackImageBitmaps,
  drawTrackShiftBackground,
} from "../../main-canvas/graphics/main-renderer.ts";
import { rangeZoomRenderer } from "../../main-canvas/graphics/range-zoom-renderer.ts";
import { drawScrollBars } from "../../main-canvas/graphics/scrollbars-renderer.ts";
import { drawTracksHeader } from "../../main-canvas/graphics/track-header-renderer.ts";
import { CANVAS_VISIBILITY_OFFSCREEN_MARGIN, GROUP_HEADER_HEIGHT } from "../../settings.ts";
import type { WorkerMessageRenderBase } from "../../track-workers/types.ts";
import type { MiniTrackGroups, Range, Track, TrackGroup, WorkerMessage } from "../../types.ts";
import type { PileupRenderingMode } from "../../types.ts";
import { getCoordinateInterval } from "../../utils/graphics/get-coordinate-interval.ts";
import { getTrackTypeBySyntenyType } from "../../utils/type-conversion.ts";
import type { GenomeViewerStore } from "../store.ts";

let rafId = -1;

// Message posting state
let isBusy = false;
let isWaiting = false;

export interface RenderingState {
  /**
   * postRenderMessage -> +1 | handleImageBitmap -> -1
   * It's definitely weak if errors happen
   */
  forceOverviewRenderFlag: number;

  // Use track id for the key
  busyList: Set<string>;
  waitingList: Set<string>;
  showTrackHeaders: boolean;
  needsRefresh: boolean;
}

export interface RenderingActions {
  refreshMain: () => void;
  renderMainCanvas: () => void;
  refreshTracks: (trackId?: string) => void;
  postTrackHoverMessage: (hoveredTrack: Track, x: number, y: number) => void;
  postTrackSelectionMessages: (trackGroupsInSelection: string[], range: Range | undefined) => void;
  postTrackHighlightMessage: (track: Track, id: number) => void;
  postTrackRenderMessage: (
    trackGroup: TrackGroup,
    track: Track,
    options: {
      basePerPixel: number;
      width: number;
      scrollX: number;
      activeSyntenyType?: SyntenyTypeEnum;
      syntenyId?: number;
      forceBaseStart?: number;
      pileupRenderingMode: PileupRenderingMode;
    },
    syntenyWorker: Worker | undefined,
  ) => void;
  forceOverviewRender: () => void;
  toggleTrackHeaderVisibility: () => void;
  getAllTracksHeight: () => number;
  initRenderBaseMessageData: (baseShift?: number) => {
    basePerPixel: number;
    basesOnScreen: number;
    coordinateInterval: number;
    ctx: CanvasRenderingContext2D;
    endBase: number;
    firstCoord: number;
    scrollX: number;
    startBase: number;
    width: number;
  };
  sendSyntenyRenderMessage: (syntenyWorker: Worker | undefined) => void;
}

export type RenderingSlice = RenderingState & RenderingActions;

export const initialRenderingState: RenderingState = {
  forceOverviewRenderFlag: 0,
  busyList: new Set<string>(),
  waitingList: new Set<string>(),
  showTrackHeaders: true,
  needsRefresh: true,
};

export const createRenderingSlice: StateCreator<GenomeViewerStore, [], [], RenderingSlice> = (set, get) => ({
  ...initialRenderingState,

  renderMainCanvas() {
    const state = get();
    const ctx = state.ctx;

    if (!ctx) {
      return;
    }

    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    // Fill the background
    ctx.fillStyle = colors.disabledBackground;
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    let offsetY = 0;
    const scrollY = state.scrollY;
    const maxWidth = state.getMaxWidth()[1];
    const originOffset = state.getOriginOffset();

    // Maps maintain insertion order, which could be important for consistent rendering
    // Instead of spreading, iterate directly
    const trackGroupsIterator = state.trackGroups.values();
    let groupResult = trackGroupsIterator.next();

    while (!groupResult.done) {
      const trackGroup = groupResult.value;
      groupResult = trackGroupsIterator.next();

      const graphics = trackGroup.graphics;
      if (!graphics.isVisible) continue;

      // Inline track filtering & mapping for speed
      const tracks: Track[] = [];

      for (const trackId of trackGroup.trackIds) {
        const track = state.tracks.get(trackId);
        if (track && track.isVisible) tracks.push(track);
      }

      // Precache frequently used properties
      const baseShift = graphics.baseShift || 0;
      const organismLength = graphics.length || 0;
      const contigLength = graphics.length;
      const scrollOffsetY = Math.floor(offsetY - state.scrollY);
      const isHighlighted = trackGroup.organismId === state.highlightedOrgId;

      ctx.save();
      ctx.translate(0, scrollOffsetY);

      // Fast sum of height
      let height = GROUP_HEADER_HEIGHT;

      for (const track of tracks) {
        height += track.height;
      }

      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, ctx.canvas.width, height);

      // Only draw visible groups
      if (scrollOffsetY + height > 0 && scrollOffsetY < ctx.canvas.height) {
        const baseData = state.initRenderBaseMessageData(baseShift);
        drawTrackShiftBackground(
          baseShift / state.basePerPixel,
          height,
          organismLength,
          ctx,
          state.scrollX,
          state.basePerPixel,
        );
        drawVerticalGridLines(height, baseShift, contigLength, baseData);
        drawTrackImageBitmaps(
          tracks,
          baseShift,
          ctx,
          state.activeSyntenyType,
          state.basePerPixel,
          state.scrollX,
          scrollOffsetY,
        );
        drawCoordinates(baseShift, contigLength, maxWidth, baseData);
        if (state.showTrackHeaders) {
          drawTracksHeader(tracks, ctx, state.hoveredTackId);
        }
        drawGroupHeader(`[${trackGroup.organismName}] - ${trackGroup.contigName}`, [], ctx, isHighlighted);

        if (tracks.length > 0 && state.hoveredGroupId !== tracks[0].groupId && state.asynchronousScrolling) {
          drawDarkOverlay(0, height, ctx);
        }
      }

      offsetY += height;
      ctx.restore();
    }

    // Draw the last grid line
    if (offsetY - scrollY > 0 && offsetY - scrollY < ctx.canvas.height) {
      drawHorizontalLine(ctx, offsetY);
    }

    drawAlignmentImageBitmaps(
      ctx,
      state.alignmentImageBitmapBasePerPixel,
      state.alignmentImageBitmap,
      state.alignmentImageBitmapScrollX,
      state.alignmentImageBitmapScrollY,
      state.basePerPixel,
      state.scrollX,
      state.scrollY,
    );

    if (offsetY - scrollY > 0 && offsetY - scrollY < ctx.canvas.height && state.asynchronousScrolling) {
      drawDarkOverlay(offsetY, ctx.canvas.height, ctx);
    }

    if (state.zoomRange) {
      rangeZoomRenderer(ctx, state.zoomRange);
    }

    if (state.selectedRange) {
      drawRangeSelection(
        ctx,
        state.selectedRange,
        state.trackGroups,
        state.tracks,
        state.trackGroupsInSelection,
        state.basePerPixel,
        state.scrollX,
        state.scrollY,
        state.leftSelectionHovering,
        state.rightSelectionHovering,
      );
    }

    drawAlignment({ ctx, ...state });

    if (state.hoverData) {
      drawHoveredFeatures(
        ctx,
        state.isDragging,
        state.hoverData,
        state.orthologousGroups,
        state.tracks,
        state.trackGroups,
        state.scrollX,
        state.scrollY,
        state.basePerPixel,
      );
    }
    drawScrollBars(
      ctx,
      state.getAllTracksHeight(),
      state.scrollX,
      scrollY,
      maxWidth,
      originOffset,
      state.hoveringHorizontalScrollbar,
      state.hoveringVerticalScrollbar,
    );
  },

  refreshMain() {
    cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => get().renderMainCanvas());

    const state = get();
    if (state.needsRefresh && state.isEveryTrackLoaded()) {
      state.needsRefresh = false;
      state.postTrackSelectionMessages(state.trackGroupsInSelection, state.selectedRange);

      // In case of loading a view, we wait for all the data to scroll to Y position
      if (state.positionToNavigate) {
        get().navigateToPosition(state.positionToNavigate);
      }

      // In case of navigation from feature table, we wait for all the data to go to the feature position and align
      if (state.featureToNavigate) {
        state.navigateToFeature(state.featureToNavigate, true);
        set({
          featureToNavigate: undefined,
        });
      }
    }
  },

  refreshTracks(trackId?: string) {
    const {
      tracks,
      trackGroups,
      activeSyntenyType,
      syntenyOrganism,
      pileupRenderingMode,
      syntenyWorker,
      postTrackRenderMessage,
    } = get();
    const baseData = get().initRenderBaseMessageData();

    // Post message to workers to render with new start
    for (const track of tracks.values()) {
      if (trackId != undefined && track.trackId !== trackId) {
        continue;
      }

      const trackGroup = trackGroups.get(track.groupId);

      if (!trackGroup) {
        continue;
      }

      if (trackGroup.graphics.isVisible && track.isVisible) {
        postTrackRenderMessage(
          trackGroup,
          track,
          {
            ...baseData,
            activeSyntenyType: activeSyntenyType,
            syntenyId: syntenyOrganism.id,
            pileupRenderingMode: pileupRenderingMode,
          },
          syntenyWorker,
        );
      }
    }
  },

  postTrackHoverMessage(hoveredTrack: Track, x: number, y: number) {
    const { basePerPixel, scrollX } = get();
    hoveredTrack.worker.postMessage({
      type: "hover",
      x,
      y,
      basePerPixel: basePerPixel,
      scrollShiftX: scrollX % (1 / basePerPixel),
    });
  },

  postTrackSelectionMessages(trackGroupsInSelection: string[], range: Range | undefined) {
    const { tracks, trackGroups, featureSelection } = get();
    set({ isSelecting: true });
    handleSelectEnd(set);
    // When there is an active selection of feature WITHOUT having the range selection indicators
    if (!range) {
      for (const { trackId, worker } of [...tracks.values()]) {
        const selectedFeatureIds = featureSelection.get(trackId) ?? [];
        worker.postMessage({
          type: "clickSelection",
          selectedFeatureIds,
        });
      }
      return;
    }

    const start = Math.min(range.start, range.stop);
    const stop = Math.max(range.start, range.stop);

    const visibleSelectedTracks = [...tracks.values()].filter((track) => track.isVisible);

    for (const { groupId, worker } of visibleSelectedTracks) {
      const baseShift = trackGroups.get(groupId)?.graphics.baseShift ?? 0;
      worker.postMessage({
        type: "selection",
        range: trackGroupsInSelection.includes(groupId)
          ? {
              start: start - baseShift,
              stop: stop - baseShift,
            }
          : // We send undefined to set the value of selectedRange to undefined in the worker, in order
            // not to have keys with empty values in featureSelection, but just no key
            undefined,
      });
    }

    get().refreshTracks();
  },

  postTrackHighlightMessage(track: Track, id: number) {
    track.worker.postMessage({
      type: "highlight",
      highlightFeatureId: id,
      requiresHighlight: true, // Flag that is directly turned to false inside the worker in order to call a
      // conditional code only once (since we loop inside the render), and to keep the highlightFeatureId defined
    });
  },

  postTrackRenderMessage(
    trackGroup: TrackGroup,
    track: Track,
    options: {
      basePerPixel: number;
      width: number;
      scrollX: number;
      activeSyntenyType?: SyntenyTypeEnum;
      syntenyId?: number;
      forceBaseStart?: number;
      pileupRenderingMode: PileupRenderingMode;
    },
    syntenyWorker: Worker | undefined,
  ) {
    const { busyList, waitingList, tracks } = get();

    if (busyList.has(track.trackId)) {
      waitingList.add(track.trackId);
      return;
    }

    busyList.add(track.trackId);

    function done(event: MessageEvent) {
      const data = event.data;
      if (data.type !== "imageBitmap") return;

      busyList.delete(track.trackId);

      // Worker being synchronous, as long as it returns, it should be done.
      track.worker.removeEventListener("message", done);

      if (waitingList.has(track.trackId)) {
        waitingList.delete(track.trackId);
        get().refreshTracks(track.trackId);
      }

      const { asynchronousScrolling } = get();

      for (const waitingTrackId of waitingList) {
        if (![...tracks.keys()].includes(waitingTrackId)) {
          waitingList.delete(waitingTrackId);
        }
      }

      for (const busyTrackId of busyList) {
        if (![...tracks.keys()].includes(busyTrackId)) {
          busyList.delete(busyTrackId);
        }
      }

      if (asynchronousScrolling || (!asynchronousScrolling && busyList.size === 0 && waitingList.size === 0)) {
        // We are calling sendSyntenyRenderMessage because all the busy logic is managed here.
        // It should be called in the handleImageBitmap function, but it will be as much confusing as having it here
        // because I didn't find a better way to do it. Honestly. I like neither choices :/
        get().sendSyntenyRenderMessage(syntenyWorker);
      }
    }

    track.worker.addEventListener("message", done);

    const messageBase: WorkerMessageRenderBase = {
      type: "render",
      token: localStorage.getItem(TOKEN_KEY) ?? "",
      trackId: track.trackId,
      contigId: trackGroup.contigId,
      length: trackGroup.graphics.length,
      baseStart:
        options.forceBaseStart ?? Math.floor(options.scrollX * options.basePerPixel - trackGroup.graphics.baseShift),
      basePerPixel: options.basePerPixel,
      mainCanvasWidth: options.width,
    };

    if (track.trackType === TrackEnum.PROTEIN_LOCATION || track.trackType === TrackEnum.TRANSCRIPT_LOCATION) {
      const syntenyId =
        options.activeSyntenyType === SyntenyTypeEnum.PROTEIN && track.trackType === TrackEnum.PROTEIN_LOCATION
          ? options.syntenyId
          : undefined;
      track.worker.postMessage({
        ...messageBase,
        text: track.text,
        color: track.color,
        synteny: !!syntenyId,
        syntenyId,
        pileupRenderingMode: options.pileupRenderingMode,
        trackType: track.trackType,
      });
    } else if (track.trackType === TrackEnum.GENE) {
      track.worker.postMessage({
        ...messageBase,
        text: track.text,
        color: track.color,
        // Having true here will cause `init` of `gene-track-worker.ts` to refetch
        // the features resp. the orthologs.
        synteny: false,
        syntenyId: options.syntenyId,
      });
    } else if (track.trackType === TrackEnum.NUCLEOTIDE_FEATURE) {
      track.worker.postMessage({
        ...messageBase,
        text: track.text,
        color: track.color,
        synteny: false,
        featureType: track.subType!,
      });
    } else if (track.trackType === TrackEnum.SEQUENCE) {
      const syntenyId = options.activeSyntenyType === SyntenyTypeEnum.GENOME_ALIGNMENT ? options.syntenyId : undefined;
      track.worker.postMessage({
        ...messageBase,
        synteny: !!syntenyId,
        syntenyId,
      });
    }
  },

  forceOverviewRender() {
    set((state) => ({
      forceOverviewRenderFlag: state.forceOverviewRenderFlag + 1,
    }));
  },

  toggleTrackHeaderVisibility() {
    set({
      showTrackHeaders: !get().showTrackHeaders,
    });
    get().refreshMain();
  },

  getAllTracksHeight() {
    const { trackGroups, tracks } = get();
    let totalHeight = 0;

    for (const [groupId, group] of trackGroups) {
      if (!group.graphics.isVisible) continue;

      totalHeight += GROUP_HEADER_HEIGHT;

      for (const track of tracks.values()) {
        if (track.groupId === groupId && track.isVisible) {
          totalHeight += track.height;
        }
      }
    }

    return totalHeight;
  },

  initRenderBaseMessageData(baseShift = 0) {
    const { ctx, canvas, basePerPixel, scrollX } = get();
    const width = canvas?.width ?? 5;

    const startBase = Math.floor(scrollX * basePerPixel - baseShift);
    const endBase = Math.ceil((scrollX + width) * basePerPixel - baseShift);
    const basesOnScreen = width * basePerPixel;

    // Calculate interval for coordinates based on screen width
    const coordinateInterval = getCoordinateInterval(width, basesOnScreen);

    const firstCoord = Math.floor(startBase / coordinateInterval) * coordinateInterval;

    return {
      basePerPixel,
      basesOnScreen,
      coordinateInterval,
      ctx: ctx!,
      endBase,
      firstCoord,
      scrollX,
      startBase,
      width,
    };
  },

  sendSyntenyRenderMessage(syntenyWorker: Worker | undefined) {
    if (isBusy) {
      isWaiting = true;
      return;
    }

    isBusy = true;

    function done() {
      isBusy = false;

      // Worker being synchronous, as long as it returns, it should be done.
      syntenyWorker?.removeEventListener("message", done);

      if (isWaiting) {
        isWaiting = false;
        get().sendSyntenyRenderMessage(syntenyWorker);
      }
    }

    syntenyWorker?.addEventListener("message", done);

    const state = get();
    const visibleTracks = [...state.tracks.values()].filter((track) => track.isVisible);
    const visibleTrackGroups = new Map(
      [...state.trackGroups.entries()].filter(([_, trackGroup]) => trackGroup.graphics.isVisible),
    );
    const miniTracks = new Map();
    for (const track of visibleTracks) {
      miniTracks.set(track.trackId, {
        trackId: track.trackId,
        trackType: track.trackType,
        height: track.height,
        baseStart: track.baseStart,
        basePerPixel: track.basePerPixel,
        isVisible: track.isVisible,
        shiftX: Math.round((state.trackGroups.get(track.groupId)?.graphics.baseShift ?? 0) / (track.basePerPixel ?? 1)),
      });
    }

    const miniTrackGroups: MiniTrackGroups = new Map();

    for (const [groupId, trackGroup] of visibleTrackGroups.entries()) {
      miniTrackGroups.set(groupId, {
        trackIds: trackGroup.trackIds,
      });
    }

    let currentY = 0;
    let lastGroupId: string | undefined;

    const visibleTrackIds = visibleTracks
      .filter((track) => {
        if (track.groupId !== lastGroupId) {
          currentY += GROUP_HEADER_HEIGHT;
          lastGroupId = track.groupId;
        }

        const height = state.canvas!.height;

        const { bottomProgress, topProgress } = verticalState(state.scrollY, height, currentY, track.height);

        currentY += track.height;

        const topPixel = Math.round(topProgress * height);
        const bottomPixel = Math.round(bottomProgress * height);

        return (
          topPixel > -CANVAS_VISIBILITY_OFFSCREEN_MARGIN || bottomPixel < height + CANVAS_VISIBILITY_OFFSCREEN_MARGIN
        );
      })
      .map((track) => track.trackId);

    syntenyWorker?.postMessage({
      type: "render",
      width: state.canvas!.width,
      height: Math.min(state.canvas!.height, state.getAllTracksHeight()),
      scrollX: state.scrollX,
      scrollY: state.scrollY,
      tracks: miniTracks,
      trackGroups: miniTrackGroups,
      visibleTrackIds: visibleTrackIds,
      basePerPixel: state.basePerPixel,
      alignedFamily: state.alignedFamily,
      activeMemberId: state.activeMemberId,
      alignmentDensity: state.alignmentDensity,
      trackType: getTrackTypeBySyntenyType(state.activeSyntenyType ?? SyntenyTypeEnum.GENOME_ALIGNMENT),
    } as WorkerMessage["SyntenicAlignmentsWorker"]["Render"]);
  },
});

const handleSelectEnd = debounce((set: StoreApi<GenomeViewerStore>["setState"]) => {
  set({ isSelecting: false });
}, 600);
